import React, {useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useForm} from 'reactjs-use-form'

import {Grid, Stack, Typography, Alert, Box} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {enqueueSnackbar} from 'notistack'

import {
  BankAccountDetailsType,
  MeDocument,
  useUpdatePaymentInformationMutation,
} from '@progyny/generated/graphql'
import RenderCountrySelector from '@progyny/pages/Budget/PartnerAgreement/helpers/RenderCountrySelector'
import AccountTypeRadio from '@progyny/pages/Budget/Reimbursement/helpers/AccountTypeRadio'
import CountryBankCodeFields from '@progyny/pages/Budget/Reimbursement/helpers/CountryBankCodeFields'
import CountryGuidelineDialog from '@progyny/pages/Budget/Reimbursement/helpers/CountryGuidelineDialog'
import {
  EMPTY_LOCAL_FIELDS,
  PaymentFormValues,
  paymentModel,
  toAccountTypeMutationValue,
} from '@progyny/pages/Budget/Reimbursement/helpers/PaymentFormModel'
import RecipientAndIbanFields from '@progyny/pages/Budget/Reimbursement/helpers/RecipientAndIbanFields'
import {useBankFormValidity} from '@progyny/pages/Budget/Reimbursement/helpers/useBankFormValidity'
import {mapToFormModel} from '@progyny/pages/Budget/Reimbursement/helpers/util'
import {SubpageOneColGrid} from '@progyny/pages/Profile/fragments/grid'
import {useMeStore} from '@progyny/store/meStore'

import {theme} from '@root/theme'

export function normalizeBankDetails(
  raw: BankAccountDetailsType
): Partial<PaymentFormValues> {
  const base: Partial<PaymentFormValues> = {
    country: typeof raw.country === 'object' ? raw.country.code : raw.country,
    accountHolderName: raw.accountHolderName,
    accountNumber: raw.accountNumber,
    swiftCode: raw.swiftCode ?? '',
    bankName: raw.bankName,
  }
  // `accountType` and `localFields` are only set when the server returned
  // them (currently the `me` query does not yet fetch these fields). Once
  // the query is extended they will pre-fill the form automatically.
  if (raw.accountType) {
    base.accountType = raw.accountType.toLowerCase()
  }
  if (raw.localFields) {
    base.localFields = raw.localFields
  }
  return base
}

const BankDetails: React.FC = () => {
  const {t} = useTranslation()
  const user = useMeStore((s) => s.user)
  const [updateBankDetails, {loading}] = useUpdatePaymentInformationMutation()
  const [showDialog, setShowDialog] = useState(false)

  const initialValues = user?.member?.bankAccountDetails
    ? mapToFormModel(
        paymentModel,
        normalizeBankDetails(user.member.bankAccountDetails)
      )
    : paymentModel

  const {values, errors, isDirty, handleOnSubmit, handleOnChange} = useForm(
    initialValues,
    handleSubmit
  )
  const {isValid} = useBankFormValidity(values as Partial<PaymentFormValues>)

  const handleCountryDialog = () => {
    setShowDialog(true)
  }

  function handleSubmit() {
    updateBankDetails({
      variables: {
        accountHolderName: values?.accountHolderName,
        accountNumber: values?.accountNumber,
        bankName: values?.bankName,
        country: values?.country,
        swiftCode: values?.swiftCode,
        accountType: toAccountTypeMutationValue(values?.accountType),
        localFields: values?.localFields || EMPTY_LOCAL_FIELDS,
      },
      refetchQueries: [{query: MeDocument}],
      awaitRefetchQueries: true,
    })
      .then(({data}) => {
        if (data && data.updatePaymentInformation) {
          if (data.updatePaymentInformation.success) {
            enqueueSnackbar({
              message: t('profile_personal_info_changes_saved'),
              variant: 'success',
            })
          } else if (data?.updatePaymentInformation?.errors) {
            const errors = data?.updatePaymentInformation?.errors
            const message = errors[0]?.messages[0]
            return enqueueSnackbar({message: message, variant: 'error'})
          }
        }
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar({
          message: t('profile_bank_details_cannot_reach_server'),
          variant: 'error',
        })
        return
      })
  }

  // We deliberately keep `swiftCode` and `localFields` when the country
  // changes: the user may switch back to a previous country and we don't want
  // them to retype values they already entered.
  const handleCustomChange = (e: {target: {name: string; value: string}}) => {
    handleOnChange({
      target: {name: e.target.name, value: e.target.value},
    } as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <Box mb={10} mt={{xs: 10, md: 15}}>
      <SubpageOneColGrid title={t('profile_payment_information')} lgSize={12}>
        <Grid
          width={{xs: '100%'}}
          mt={2}
          container
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'start'}
        >
          <Grid
            component={'form'}
            item
            xs={12}
            md={7}
            onSubmit={handleOnSubmit}
          >
            <Stack flexDirection={'column'} mb={3}>
              <Typography fontSize={16} fontWeight={600}>
                {t('profile_bank_details_select_country')}
              </Typography>
              <Box pt={'4px'}>
                <Typography
                  display={'inline'}
                  fontSize={16}
                  color={theme.palette.other.brandColors.lightGray}
                >
                  {t('profile_bank_details_country_note')}
                </Typography>{' '}
                &nbsp;
                <Typography
                  sx={{cursor: 'pointer'}}
                  color={theme.palette.other.brandColors.primary}
                  onClick={handleCountryDialog}
                  fontSize={14}
                  fontWeight={500}
                  display={'inline'}
                >
                  {t('common_learn_more')}
                </Typography>
              </Box>
              <CountryGuidelineDialog
                showDialog={showDialog}
                setShowDialog={setShowDialog}
              />
            </Stack>
            <RenderCountrySelector
              handleOnChange={handleCustomChange}
              values={values}
              field='country'
              id='bank-country'
              label={t('budget_bank_country')}
              isReview={false}
            />

            <AccountTypeRadio
              value={values?.accountType as string}
              onChange={handleOnChange}
            />

            <Stack mt={7}>
              <Typography fontSize={16} fontWeight={600}>
                {t('profile_bank_details_fill_details')}
              </Typography>

              <RecipientAndIbanFields
                values={values}
                errors={errors}
                handleOnChange={handleOnChange}
              />

              <CountryBankCodeFields
                values={values}
                errors={errors}
                handleOnChange={handleOnChange}
              />
            </Stack>
            <Alert
              severity='info'
              sx={{
                backgroundColor: '#F4F6FF',
                my: 7,
                '& .MuiSvgIcon-root': {
                  color: theme.palette.other.brandColors.primary,
                },
              }}
            >
              <Trans i18nKey='profile_bank_details_additional_info_note' />
            </Alert>
            <Box
              display='flex'
              mt={4}
              alignItems='center'
              justifyContent='start'
              width={{xs: '100%', sm: 'auto'}}
            >
              <Box
                display='flex'
                justifyContent='center'
                width={{xs: '100%', sm: 'auto'}}
              >
                <PGButton
                  type='submit'
                  loading={loading}
                  disabled={!isDirty || loading || !isValid}
                  label={t('common_save')}
                  sx={{width: {xs: '100%', sm: 'auto'}}}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </SubpageOneColGrid>
    </Box>
  )
}

export default BankDetails
